import * as React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays } from '@fortawesome/free-solid-svg-icons';
import { CalendarPanel } from './CalendarPanel';
import { TimePanel } from './TimePanel';
import 'react-day-picker/dist/style.css';

type DatePickerProps = {
	id?: string;
	name?: string;
	value?: Date;
	onChange: (date?: Date) => void;
	placeholder?: string;
	required?: boolean;
	readOnly?: boolean;
	showTime?: boolean;
};

function formatDateValue(value: Date | undefined, showTime: boolean): string {
	if (!value) return '';
	// Preserve previous locale-aware display: use toLocaleDateString() as before
	const datePart = value.toLocaleDateString();
	if (showTime) {
		const h = value.getHours().toString().padStart(2, '0');
		const min = value.getMinutes().toString().padStart(2, '0');
		return `${datePart} - ${h}h${min}`;
	}
	return datePart;
}

function autoFormatDate(raw: string): string {
	const digits = raw.replace(/\D/g, '').slice(0, 8);
	let result = digits.slice(0, 2);
	if (digits.length > 2) result += '/' + digits.slice(2, 4);
	if (digits.length > 4) result += '/' + digits.slice(4, 8);
	return result;
}

function autoFormatDateTime(raw: string): string {
	const digits = raw.replace(/\D/g, '').slice(0, 12);
	let result = digits.slice(0, 2);
	if (digits.length > 2) result += '/' + digits.slice(2, 4);
	if (digits.length > 4) result += '/' + digits.slice(4, 8);
	if (digits.length > 8) result += ' - ' + digits.slice(8, 10);
	if (digits.length > 10) result += 'h' + digits.slice(10, 12);
	return result;
}

function parseDate(str: string): Date | undefined {
	const match = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
	if (!match) return undefined;
	const d = parseInt(match[1]);
	const mo = parseInt(match[2]) - 1;
	const y = parseInt(match[3]);
	const date = new Date(y, mo, d);
	if (
		date.getDate() !== d ||
		date.getMonth() !== mo ||
		date.getFullYear() !== y
	)
		return undefined;
	return date;
}

function parseDateTime(str: string): Date | undefined {
	const match = str.match(/^(\d{2})\/(\d{2})\/(\d{4}) - (\d{2})h(\d{2})$/);
	if (!match) return undefined;
	const d = parseInt(match[1]);
	const mo = parseInt(match[2]) - 1;
	const y = parseInt(match[3]);
	const h = parseInt(match[4]);
	const min = parseInt(match[5]);
	if (h > 23 || min > 59) return undefined;
	const date = new Date(y, mo, d, h, min);
	if (
		date.getDate() !== d ||
		date.getMonth() !== mo ||
		date.getFullYear() !== y
	)
		return undefined;
	return date;
}

export function DatePicker({
	value,
	onChange,
	placeholder,
	id,
	name,
	required,
	readOnly = false,
	showTime = false,
}: DatePickerProps) {
	const [open, setOpen] = React.useState(false);
	const [flash, setFlash] = React.useState(false);
	const [inputText, setInputText] = React.useState(() =>
		formatDateValue(value, showTime)
	);

	const isEditingRef = React.useRef(false);
	const prevValueRef = React.useRef<Date | undefined>(value);

	// Synchronise l'input quand la valeur change depuis le calendrier (pas depuis la saisie clavier)
	React.useEffect(() => {
		if (isEditingRef.current) return;
		if (prevValueRef.current?.getTime() === value?.getTime()) return;
		prevValueRef.current = value;

		if (value !== undefined) {
			setFlash(true);
			const timer = setTimeout(() => {
				setInputText(formatDateValue(value, showTime));
				setFlash(false);
			}, 150);
			return () => clearTimeout(timer);
		} else {
			setInputText('');
		}
	}, [value, showTime]);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const formatted = showTime
			? autoFormatDateTime(e.target.value)
			: autoFormatDate(e.target.value);
		setInputText(formatted);
	};

	const handleFocus = () => {
		isEditingRef.current = true;
	};

	const handleBlur = () => {
		isEditingRef.current = false;
		if (inputText === '') {
			if (value !== undefined) onChange(undefined);
			return;
		}
		const parsed = showTime ? parseDateTime(inputText) : parseDate(inputText);
		if (!parsed) {
			// Saisie invalide : on revient à la dernière valeur valide
			setInputText(formatDateValue(value, showTime));
			return;
		}
		// Quitter le champ sans changer le jour ne doit pas réécrire la date.
		if (
			value &&
			value.getFullYear() === parsed.getFullYear() &&
			value.getMonth() === parsed.getMonth() &&
			value.getDate() === parsed.getDate() &&
			(!showTime ||
				(value.getHours() === parsed.getHours() &&
					value.getMinutes() === parsed.getMinutes()))
		) {
			setInputText(formatDateValue(value, showTime));
			return;
		}
		onChange(parsed);
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			(e.target as HTMLInputElement).blur();
		}
	};

	const handleToday = () => {
		const today = new Date();
		today.setHours(12, 0, 0, 0);
		onChange(today);
		if (!showTime) setOpen(false);
	};

	const handleClear = () => {
		onChange(undefined);
		setOpen(false);
	};

	const defaultPlaceholder =
		placeholder ?? (showTime ? 'JJ/MM/AAAA - HHhMM' : 'JJ/MM/AAAA');

	return (
		<div className="flex items-center gap-2">
			<input
				type="text"
				value={inputText}
				placeholder={defaultPlaceholder}
				onChange={handleInputChange}
				onFocus={handleFocus}
				onBlur={handleBlur}
				onKeyDown={handleKeyDown}
				maxLength={showTime ? 18 : 10}
				className={`flex-1 h-10 border p-2 rounded bg-gray-100
					focus:outline-none focus:ring-2 focus:ring-blue-500
					placeholder:italic
					transition-colors duration-200
					${flash ? 'text-gray-500' : 'text-black'}
					${readOnly ? 'text-gray-400 cursor-not-allowed bg-gray-200' : ''}`}
				id={id}
				name={name}
				required={required}
				disabled={readOnly}
			/>
			{!readOnly && (
				<FontAwesomeIcon
					icon={faCalendarDays}
					className="text-gray-400 cursor-pointer hover:text-blue-500 transition-colors"
					onMouseDown={(e) => {
						// Empêche le blur de l'input avant que le clic ne soit traité
						e.preventDefault();
					}}
					onClick={() => setOpen(true)}
				/>
			)}

			{open && (
				<div
					className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-[1px]"
					onClick={(e) => {
						if (e.target === e.currentTarget) setOpen(false);
					}}
				>
					{showTime ? (
						<div className="bg-white rounded shadow p-4 max-w-2xl flex gap-4">
							<div>
								<CalendarPanel
									value={value}
									showTime={showTime}
									onSelect={(date: Date) => {
										onChange(date);
									}}
								/>
							</div>

							{/* Bloc TimePanel + actions */}
							<div>
								<TimePanel value={value} onChange={onChange} />
								<div className="flex gap-2 mt-2">
									<button
										type="button"
										onClick={handleToday}
										className="flex-1 px-2 py-1 h-10 bg-gray-200 rounded hover:bg-gray-300"
									>
										Aujourd&apos;hui
									</button>
									<button
										type="button"
										onClick={handleClear}
										className="flex-1 px-2 py-1 h-10 bg-red-200 text-red-800 rounded hover:bg-red-300"
									>
										Effacer
									</button>
								</div>
								<button
									type="button"
									onClick={() => setOpen(false)}
									className="mt-2 w-full px-2 py-1 bg-blue-200 text-blue-800 rounded hover:bg-blue-300"
								>
									Valider
								</button>
							</div>
						</div>
					) : (
						<div className="bg-white rounded shadow p-4 max-w-2xl flex flex-col items-center">
							{/* Calendrier pleine largeur */}
							<CalendarPanel
								value={value}
								showTime={showTime}
								onSelect={(date: Date) => {
									onChange(date);
									setOpen(false);
								}}
							/>

							{/* Boutons Aujourd’hui / Effacer sous le calendrier, prenant chacun 50% de la largeur */}
							<div className="flex gap-2 mt-2 w-full">
								<button
									type="button"
									onClick={handleToday}
									className="flex-1 px-2 py-1 h-10 bg-gray-200 rounded hover:bg-gray-300"
								>
									Aujourd&apos;hui
								</button>
								<button
									type="button"
									onClick={handleClear}
									className="flex-1 px-2 py-1 h-10 bg-red-200 text-red-800 rounded hover:bg-red-300"
								>
									Effacer
								</button>
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
}

export default DatePicker;
